/**
 * IATA numeric delay codes for a charter desk (AHM 730).
 *
 * Every row below was checked against the public EUROCONTROL Aviation
 * Intelligence Portal list, which cites IATA, Airport Handling Manual —
 * Standard IATA Delay Codes (AHM 730), 2012:
 * https://ansperformance.eu/definition/iata-delay-codes/
 *
 * The same numbers and labels were cross-checked, where that PDF states
 * them, against Aeroporti di Roma's published table "Causali di ritardo
 * IATA (AHM 730 Edition 37, January 2017)":
 * https://www.adr.it/documents/10157/13335255/causali+ritardo+IATA.pdf
 *
 * Codes that could not be read on that public list are not in this table.
 * Checked again on 2026-11-03 against that portal page. Code 74 is not on it,
 * so it is not in this table. Airline-internal 00–05, 06, 09, cargo and mail
 * 21–29, and damage / EDP 51–58 are omitted on purpose: they are not the
 * charter subset this desk files.
 */
export type DelayCodeGroup =
  | "Passenger and baggage"
  | "Aircraft and ramp handling"
  | "Technical and aircraft equipment"
  | "Flight operations and crewing"
  | "Weather"
  | "Air traffic flow management"
  | "Airport and governmental authorities"
  | "Reactionary"
  | "Miscellaneous";

export type DelayCode = {
  code: string;
  /** IATA alphabetic code from the same row. */
  alpha: string;
  group: DelayCodeGroup;
  label: string;
};

export const IATA_DELAY_CODES: readonly DelayCode[] = [
  { code: "11", alpha: "PD", group: "Passenger and baggage", label: "Late check-in, acceptance after deadline" },
  { code: "12", alpha: "PL", group: "Passenger and baggage", label: "Late check-in, congestions in check-in area" },
  { code: "13", alpha: "PE", group: "Passenger and baggage", label: "Check-in error, passenger and baggage" },
  { code: "14", alpha: "PO", group: "Passenger and baggage", label: "Oversales, booking errors" },
  { code: "15", alpha: "PH", group: "Passenger and baggage", label: "Boarding, discrepancies and paging, missing checked-in passenger" },
  { code: "16", alpha: "PS", group: "Passenger and baggage", label: "Commercial publicity / passenger convenience, VIP, press, ground meals and missing personal items" },
  { code: "17", alpha: "PC", group: "Passenger and baggage", label: "Catering order, late or incorrect order given to supplier" },
  { code: "18", alpha: "PB", group: "Passenger and baggage", label: "Baggage processing, sorting" },
  { code: "19", alpha: "PW", group: "Passenger and baggage", label: "Reduced mobility, boarding / deboarding of passengers with reduced mobility" },

  { code: "31", alpha: "GD", group: "Aircraft and ramp handling", label: "Aircraft documentation late / inaccurate, weight and balance, general declaration, pax manifest" },
  { code: "32", alpha: "GL", group: "Aircraft and ramp handling", label: "Loading / unloading, bulky, special load, cabin load, lack of loading staff" },
  { code: "33", alpha: "GE", group: "Aircraft and ramp handling", label: "Loading equipment, lack of or breakdown, lack of staff" },
  { code: "34", alpha: "GS", group: "Aircraft and ramp handling", label: "Servicing equipment, lack of or breakdown, lack of staff" },
  { code: "35", alpha: "GC", group: "Aircraft and ramp handling", label: "Aircraft cleaning" },
  { code: "36", alpha: "GF", group: "Aircraft and ramp handling", label: "Fuelling / defuelling, fuel supplier" },
  { code: "37", alpha: "GB", group: "Aircraft and ramp handling", label: "Catering, late delivery or loading" },
  { code: "38", alpha: "GU", group: "Aircraft and ramp handling", label: "ULD, lack of or serviceability" },
  { code: "39", alpha: "GT", group: "Aircraft and ramp handling", label: "Technical equipment, lack of or breakdown, lack of staff" },

  { code: "41", alpha: "TD", group: "Technical and aircraft equipment", label: "Aircraft defects" },
  { code: "42", alpha: "TM", group: "Technical and aircraft equipment", label: "Scheduled maintenance, late release" },
  { code: "43", alpha: "TN", group: "Technical and aircraft equipment", label: "Non-scheduled maintenance, special checks and / or additional works beyond normal maintenance schedule" },
  { code: "44", alpha: "TS", group: "Technical and aircraft equipment", label: "Spares and maintenance equipment, lack of or breakdown" },
  { code: "45", alpha: "TA", group: "Technical and aircraft equipment", label: "AOG spares, to be carried to another station" },
  { code: "46", alpha: "TC", group: "Technical and aircraft equipment", label: "Aircraft change, for technical reasons" },
  { code: "47", alpha: "TL", group: "Technical and aircraft equipment", label: "Stand-by aircraft, lack of planned stand-by aircraft for technical reasons" },
  { code: "48", alpha: "TV", group: "Technical and aircraft equipment", label: "Scheduled cabin configuration / version adjustments" },

  { code: "61", alpha: "FP", group: "Flight operations and crewing", label: "Flight plan, late completion or change of, flight documentation" },
  { code: "62", alpha: "FF", group: "Flight operations and crewing", label: "Operational requirements, fuel, load alteration" },
  { code: "63", alpha: "FT", group: "Flight operations and crewing", label: "Late crew boarding or departure procedures, other than connection and standby" },
  { code: "64", alpha: "FS", group: "Flight operations and crewing", label: "Flight deck crew shortage, sickness, awaiting standby, flight time limitations, crew meals, valid visa, health documents" },
  { code: "65", alpha: "FR", group: "Flight operations and crewing", label: "Flight deck crew special request, not within operational requirements" },
  { code: "66", alpha: "FL", group: "Flight operations and crewing", label: "Late cabin crew boarding or departure procedures, other than connection and standby" },
  { code: "67", alpha: "FC", group: "Flight operations and crewing", label: "Cabin crew shortage, sickness, awaiting standby, flight time limitations, crew meals, valid visa, health documents" },
  { code: "68", alpha: "FA", group: "Flight operations and crewing", label: "Cabin crew error or special request, not within operational requirements" },
  { code: "69", alpha: "FB", group: "Flight operations and crewing", label: "Captain request for security check, extraordinary" },

  { code: "71", alpha: "WO", group: "Weather", label: "Weather at departure station" },
  { code: "72", alpha: "WT", group: "Weather", label: "Weather at destination station" },
  { code: "73", alpha: "WR", group: "Weather", label: "Weather en route or alternate" },
  { code: "75", alpha: "WI", group: "Weather", label: "De-icing of aircraft, removal of ice and / or snow, frost prevention excluding unserviceability of equipment" },
  { code: "76", alpha: "WS", group: "Weather", label: "Removal of snow, ice, water and sand from airport" },
  { code: "77", alpha: "WG", group: "Weather", label: "Ground handling impaired by adverse weather conditions" },

  { code: "81", alpha: "AT", group: "Air traffic flow management", label: "ATFM due to ATC en-route demand / capacity, standard demand / capacity problems" },
  { code: "82", alpha: "AX", group: "Air traffic flow management", label: "ATFM due to ATC staff / equipment en-route, reduced capacity caused by industrial action or staff shortage, equipment failure, military exercise or extraordinary demand" },
  { code: "83", alpha: "AE", group: "Air traffic flow management", label: "ATFM due to restriction at destination airport" },
  { code: "84", alpha: "AW", group: "Air traffic flow management", label: "ATFM due to weather at destination" },

  { code: "85", alpha: "AS", group: "Airport and governmental authorities", label: "Mandatory security" },
  { code: "86", alpha: "AG", group: "Airport and governmental authorities", label: "Immigration, customs, health" },
  { code: "87", alpha: "AF", group: "Airport and governmental authorities", label: "Airport facilities, parking stands, ramp congestion, lighting, buildings, gate limitations" },
  { code: "88", alpha: "AD", group: "Airport and governmental authorities", label: "Restrictions at airport of destination" },
  { code: "89", alpha: "AM", group: "Airport and governmental authorities", label: "Restrictions at airport of departure, with or without ATFM restrictions" },

  { code: "91", alpha: "RL", group: "Reactionary", label: "Load connection, awaiting load from another flight" },
  { code: "92", alpha: "RT", group: "Reactionary", label: "Through check-in error, passenger and baggage" },
  { code: "93", alpha: "RA", group: "Reactionary", label: "Aircraft rotation, late arrival of aircraft from another flight or previous sector" },
  { code: "94", alpha: "RS", group: "Reactionary", label: "Cabin crew rotation, awaiting cabin crew from another flight" },
  { code: "95", alpha: "RC", group: "Reactionary", label: "Crew rotation, awaiting crew from another flight" },
  { code: "96", alpha: "RO", group: "Reactionary", label: "Operations control, re-routing, diversion, consolidation, aircraft change for reasons other than technical" },

  { code: "97", alpha: "MI", group: "Miscellaneous", label: "Industrial action with own airline" },
  { code: "98", alpha: "MO", group: "Miscellaneous", label: "Industrial action outside own airline, excluding ATS" },
  { code: "99", alpha: "MX", group: "Miscellaneous", label: "Other reason, not matching any code above" },
] as const;

const BY_CODE = new Map(IATA_DELAY_CODES.map((row) => [row.code, row]));

export function delayCodeGroups(): DelayCodeGroup[] {
  const seen: DelayCodeGroup[] = [];
  for (const row of IATA_DELAY_CODES) {
    if (!seen.includes(row.group)) seen.push(row.group);
  }
  return seen;
}

export function delayCodesInGroup(group: DelayCodeGroup): DelayCode[] {
  return IATA_DELAY_CODES.filter((row) => row.group === group);
}

export function lookupDelayCode(raw: unknown): DelayCode | null {
  const code = String(raw ?? "").trim().replace(/^0+(?=\d)/, "").padStart(2, "0");
  return BY_CODE.get(code) ?? null;
}

/**
 * Known codes stay as filed. Anything else is stored as 99, with the
 * original token kept for the remarks. The desk does not reject the report.
 */
export function normalizeReportedDelayCode(raw: unknown): { code: string; rawCode?: string } {
  const token = String(raw ?? "").trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  const hit = lookupDelayCode(token);
  if (hit) return { code: hit.code };
  return { code: "99", rawCode: token.slice(0, 8) || "UNKNOWN" };
}

export function delayCodeLabel(code: string): string {
  return lookupDelayCode(code)?.label ?? "Other reason, not matching any code above";
}

/** Short reason token for a telex. Letters and digits only. */
export function delayShortReason(code: string): string {
  const row = lookupDelayCode(code);
  const source = row ? `${row.alpha} ${row.label}` : "OTHER";
  return source
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 40);
}
